import { Languages, Treesponse } from "@tree-company/collect-js-client";
import { Providers, ProviderScore, SCORES } from "./data/scores";

type RankingType = Array<{ name: string; percentage: number }>;
type ResultType = {
  rankings: RankingType;
  weights: Array<{ key: keyof ProviderScore; weight: number }>;
  totalWeight: number;
};

export function calculateResult(treesponse: Treesponse): ResultType {
  const scores = SCORES[treesponse.language as keyof typeof SCORES];
  const weights: ProviderScore = {
    price: (treesponse.getAnswer("price") as number) - 1,
    origin: (treesponse.getAnswer("origin") as number) - 1,
    source: (treesponse.getAnswer("source") as number) - 1,
    customer_satisfaction:
      (treesponse.getAnswer("customer_satisfaction") as number) - 1,
    invests_in_renewable:
      (treesponse.getAnswer("invests_in_renewable") as number) - 1,
    stable_price: (treesponse.getAnswer("stable_price") as number) - 1,
    adjusted_formula: (treesponse.getAnswer("adjusted_formula") as number) - 1,
  };

  const totalWeight = Object.values(weights).reduce((acc, cur) => acc + cur, 0);

  // Score per provider = sum(score * weight) / sum(weights)
  // Weighted average: criteria with weight 0 drop out automatically
  const result = Object.entries(scores).reduce<RankingType>(
    (acc, [provider, score]) => {
      const percentage =
        Object.entries(score).reduce((acc: number, [key, keyScore]) => {
          return acc + keyScore * weights[key as keyof typeof weights];
        }, 0) / totalWeight;
      acc.push({ name: provider as keyof typeof Providers, percentage });
      return acc;
    },
    [] as RankingType,
  );

  const normalizedResult = result
    .map((obj) => {
      obj.percentage = Math.round(obj.percentage * 100);
      return obj;
    })
    .sort((r1, r2) => r2.percentage - r1.percentage);

  const sortedWeights = Object.entries(weights)
    .map(([key, weight]) => ({ key: key as keyof ProviderScore, weight }))
    .filter(({ weight }) => weight > 0)
    .sort((w1, w2) => w2.weight - w1.weight);

  return { weights: sortedWeights, rankings: normalizedResult, totalWeight };
}

declare const NETLIFY_DEPLOY_PRIME_URL: string;

// Added NETLIFY_DEPLOY_PRIME_URL, so this works with branch deploys too
let redirectBaseUrl = import.meta.env.VITE_REDIRECT_URL;
if (redirectBaseUrl === undefined) {
  if (import.meta.env.DEV) {
    redirectBaseUrl = "http://localhost:8080";
  } else if (NETLIFY_DEPLOY_PRIME_URL) {
    redirectBaseUrl = NETLIFY_DEPLOY_PRIME_URL.replace(
      "seacoop-survey",
      "seacoop-result",
    );
  } else {
    redirectBaseUrl = "https://seacoop-result.netlify.app";
  }
}

// Example `profiles/engie/?top_0=engie;20&top_1=bolt;10&top_2=mega;11&top_3=engie;2&top_4=ecopower;1&score_0=origin;10;20&score_1=source;10;20&score_2=fair_price;30;40`
// url param top_0, top_1, ... contains the top 5 matches in the form of `NAME;PERCENTAGE`
// url param score_0, score_1, ... contains the 3 highest ranked scores to show, in the form of:
// `SCORE_KEY;VALUE_FROM_PROVIDER_1;VALUE_FROM_PROVIDER_2`
export function constructRedirectURL(
  language: Languages,
  result: ResultType,
): string {
  const scores = SCORES[language as keyof typeof SCORES];
  const params = new URLSearchParams();
  result.rankings.slice(0, 5).forEach((result, index) => {
    params.set(`top_${index}`, `${result.name};${result.percentage}`);
  });
  result.weights.forEach((weight, index) => {
    const value1 = Math.round(
      scores[result.rankings[0].name as keyof typeof scores][weight.key] * 100,
    );
    const value2 = Math.round(
      scores[result.rankings[1].name as keyof typeof scores][weight.key] * 100,
    );
    params.set(`score_${index}`, `${weight.key};${value1};${value2}`);
  });
  params.set(
    "tip-invest",
    ["ecopower", "cociter"].includes(result.rankings[0].name) ? "1" : "0",
  );

  const url = new URL(
    `/${language}/${result.rankings[0].name}/`,
    redirectBaseUrl,
  );
  url.search = params.toString();
  return url.toString();
}
